import type { AlarmEvent, CalibrationRecord, ResourceKpi, Sample } from '@ctcells/contract'
import type { ModuleKey } from '@ctcells/core'
import {
  BATCH_HISTORY,
  BATCH_LIST,
  BATCH_RUNNING,
  CALIBRATIONS,
  DEFAULT_SCENARIO,
  HISTORY,
  MODULE_DIAGNOSTIC_HISTORY,
  RECIPE_LIST,
  RECIPES,
  RESOURCE_KPI,
  SAMPLES,
  TRAY_LOADS,
  TRAY_UNLOADS,
  VISION_FEEDS,
} from '@ctcells/fixtures'
import { t } from '@ctcells/i18n'

import { historyForBatch, traysForBatch } from './batches/batchDetailModel.ts'
import { sampleGroupFacts } from './recipes/recipeForm.ts'
import { activeModules } from './moduleService.ts'
import type { BatchDetailData } from './viewTypes.ts'

/**
 * 🔴 **운영 화면(모니터링 · 작업 · 이력 · 알람)이 목 데이터와 만나는 지점.**
 *
 * 화면 컴포넌트는 전부 props 로 값을 받고, 그 기본값만 여기서 채운다.
 * API 가 붙으면 **이 파일 하나를 쿼리 결과로 갈아끼우고 지운다** — 화면 코드는 한 줄도 안 바뀐다.
 *
 * ⚠ 리소스·시스템 화면(시료 · 레시피 · 랩웨어 · 모듈 · 로그 · 관리)은 여기를 거치지 않고
 *   `@ctcells/fixtures` 를 직접 기본값으로 쓴다. 그쪽은 계약 타입을 그대로 내보내면 되지만
 *   여기 있는 값은 응답 여러 개를 화면이 읽는 꼴(`viewTypes.ts`)로 **합쳐야** 나오기 때문이다.
 *
 * 기본 시나리오는 `DEFAULT_SCENARIO`(실행 중)이다. 다른 상태를 보려면 화면에 props 로 넘긴다 —
 * 예: `<MonitoringScreen {...SCENARIO_DISCONNECTED} />` 로 stale 규약을 확인할 수 있다.
 */

/*
 * 🔴 **목에서도 폐기 모듈을 걷는다.** 시나리오에 폐기 1대를 «일부러» 섞어 뒀고
 *    (`fixtures/src/modules.ts` — 실물이 그렇다), 거르는 층이 실물 컨테이너와 같아야
 *    「폐기를 거른다」가 목에서만 통과하는 일이 없다.
 */
export const DEMO_MODULES = activeModules(DEFAULT_SCENARIO.modules)
export const DEMO_CHAMBER_ROWS = DEFAULT_SCENARIO.chambers
export const DEMO_TRAY_LAYOUT = DEFAULT_SCENARIO.tray
export const DEMO_TRAY_SLOTS = DEFAULT_SCENARIO.tray.map((cell) => cell.slot)
export const DEMO_VISION_FEEDS = VISION_FEEDS
export const DEMO_ALARMS: readonly AlarmEvent[] = DEFAULT_SCENARIO.alarms
export const DEMO_RESOURCE_KPI: ResourceKpi = RESOURCE_KPI
export const DEMO_BATCH_LIST = BATCH_LIST
export const DEMO_BATCH_HISTORY = BATCH_HISTORY

/**
 * 아직 어느 작업에도 안 들어간 시료 — 새 작업 화면의 선택 후보.
 * 🔴 **판정은 `batch_id` 하나다**(`unassignable_samples` — 서버가 그것만 본다).
 *    `== null` 로 본다 — 서버는 키를 주고 값만 `null` 이다.
 */
export const DEMO_UNASSIGNED_SAMPLES: readonly Sample[] = SAMPLES.filter(
  (sample) => sample.batch_id == null,
)

/**
 * 시료 전건 — **시료 흐름 격자의 demo 입력**(2026-09-21).
 * ⚠ 흐름은 이 목록에서 **도는 작업의 시료만** 골라 쓴다(`samplesOfBatch`). 목록 전체를 그리지
 *   않는 것이 live 와 같은 규율이다 — 끝난 작업의 시료가 「완료」 줄로 섞이지 않게.
 */
export const DEMO_SAMPLES: readonly Sample[] = SAMPLES

/**
 * 작업 상세 한 벌 — 🔴 **전부 「응답에 있는 값으로 거르기」다.**
 *
 * 실물 컨테이너(`BatchDetailContainer`)와 **같은 고르기 함수**를 쓴다(`batchDetailModel.ts`) —
 * 목이 실물보다 순하면 거기서만 통하는 화면이 생긴다(`CLAUDE.md` fixture 규칙).
 *
 * ⚠ 수거 원장에 이 배치 행이 **없다**. 실행 중 배치라 아직 수거가 열리지 않은 것이고,
 *   그래서 데모가 「빈 상태」 경계를 그대로 보여 준다 — 억지로 채우지 않는다.
 */
const RUNNING_RECIPE = RECIPE_LIST.find((row) => row.recipe_id === BATCH_RUNNING.recipe_id)

/**
 * 이벤트 원장(`event_type=event`) 목 — **상시 작업 줄의 이벤트 칸**이 쓴다.
 * ⚠ 작업 상세에서는 이벤트 탭을 걷어(2026-09-30) `DEMO_BATCH_DETAIL` 에서 빠졌다. 고르기는 부르는 쪽이
 *   `historyForBatch(…, batchId)` 로 한다.
 */
// 한 작업의 이벤트 원장 셋 — `event` + `batch_record` + `audit`(`@ctcells/api` `BATCH_EVENT_LEDGERS` · 사람이 누른 시작 · 중지는 감사에만 있다)
export const DEMO_BATCH_EVENTS = HISTORY.filter(
  (entry) =>
    entry.event_type === 'event' ||
    entry.event_type === 'batch_record' ||
    entry.event_type === 'audit',
)

export const DEMO_BATCH_DETAIL: BatchDetailData = {
  batch: BATCH_RUNNING,
  recipeName: RUNNING_RECIPE?.name ?? t('mask.none'),
  samples: SAMPLES.filter((sample) => sample.batch_id === BATCH_RUNNING.batch_id),
  /* 실물 컨테이너와 같은 함수(`sampleGroupFacts`) — 레시피 본문은 목록 행이 아니라 전문(`RECIPES`)에 있다 */
  sampleGroups: sampleGroupFacts(
    RECIPES.find((recipe) => recipe.recipe_id === BATCH_RUNNING.recipe_id)?.payload ?? {},
  ).groups,
  // 🔴 명령 카드는 **감사 원장**이다 — `batch:` 접두어로 고른다
  commands: historyForBatch(
    HISTORY.filter((entry) => entry.event_type === 'audit'),
    BATCH_RUNNING.batch_id,
  ),
  load: traysForBatch(TRAY_LOADS, BATCH_RUNNING.batch_id),
  unload: traysForBatch(TRAY_UNLOADS, BATCH_RUNNING.batch_id),
}

/**
 * 모듈 패드 › 정비 탭이 쓰는 값.
 *
 * ⚠ **계약 없음.** 보정 목록·자가 진단 이력 경로가 orchestrator OpenAPI 에 0건이라
 *   fixture 가 쥐고 있다(`packages/fixtures/src/modules.ts` 주석과 같은 사정).
 *   보정 버튼의 이름을 코드에 한글로 박지 않고 **보정 기록의 종류에서 가져온다** —
 *   실물이 붙으면 그 목록이 그대로 버튼 목록이 된다.
 */
export const DEMO_MAINTENANCE: Record<
  ModuleKey,
  { calibrations: readonly CalibrationRecord[]; lastDiagnosticText: string }
> = {
  cent: maintenanceOf('cent'),
  asm: maintenanceOf('asm'),
  stain: maintenanceOf('stain'),
}

function maintenanceOf(moduleKey: ModuleKey): {
  calibrations: readonly CalibrationRecord[]
  lastDiagnosticText: string
} {
  const latest = MODULE_DIAGNOSTIC_HISTORY[moduleKey]?.[0]
  return {
    calibrations: CALIBRATIONS.filter((record) => record.target === moduleKey),
    lastDiagnosticText:
      latest === undefined ? t('mask.none') : `${latest.at_text} · ${latest.detail ?? ''}`.trim(),
  }
}
